import { useLazyQuery, useMutation } from '@apollo/client';
import { Rights } from '@dokucoach/common-types';
import { useContextAuthManager } from 'components/AuthManager';
import Button from 'components/Button';
import ConfirmationModal from 'components/ConfirmationModal';
import ResidentGroupModal, {
  ResidentGroupUpdateValues,
  ResidentGroupValues,
} from 'components/ResidentGroupModal';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GET_HOUSE_DIVISIONS } from 'services/houseDivisions';
import {
  CREATE_RESIDENT_GROUP,
  REMOVE_RESIDENT_GROUP,
  UPDATE_RESIDENT_GROUP,
} from 'services/mutation/residentGroups';
import {
  GET_RESIDENT_GROUPS_AND_RESIDENTS,
  ResidentGroupPropType,
  ResidentPropType,
} from 'services/residentGroups';
import { useContextSnackbar } from '../../../Snackbar/context';
import { useContextLayout } from '../../context';
import AsideMenu from '../AsideMenu';
import { Footer } from '../common/styled-components';

interface HouseDivision {
  id: string;
  name: string;
  parentId: string | null;
  residentId: string | null;
}

export const DEFAULT_GROUP_ID = 'all-residents';

export const HOUSE_DIVISION_GROUP_ID = 'house-division-';

const DashboardAside = () => {
  const { t } = useTranslation();
  const { showApiError } = useContextSnackbar();
  const { hasPermission } = useContextAuthManager();

  const { residentGroups, setResidentGroups, shouldRefetchGroups, setShouldRefetchGroups } =
    useContextLayout();
  const [
    getResidentGroups,
    { data: residentGroupsData, error: residentGroupsError, refetch: residentGroupsRefetch },
  ] = useLazyQuery(GET_RESIDENT_GROUPS_AND_RESIDENTS);
  const [
    getHouseDivisionsByType,
    { data: houseDivisionsData, error: houseDivisionsError, refetch: houseDivisionsRefetch },
  ] = useLazyQuery(GET_HOUSE_DIVISIONS);
  const [createResidentGroup] = useMutation(CREATE_RESIDENT_GROUP);
  const [updateResidentGroup] = useMutation(UPDATE_RESIDENT_GROUP);
  const [removeResidentGroup] = useMutation(REMOVE_RESIDENT_GROUP);

  const [showGroupModal, setShowGroupModal] = useState<boolean>(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [confirmationText, setConfirmationText] = useState<string>('');
  const [groupToEdit, setGroupToEdit] = useState<ResidentGroupPropType | undefined>();

  useEffect(() => {
    if (shouldRefetchGroups && residentGroupsRefetch && houseDivisionsRefetch) {
      Promise.all([residentGroupsRefetch(), houseDivisionsRefetch()]).then(() => {
        setShouldRefetchGroups(undefined);
      });
    }
  }, [shouldRefetchGroups, residentGroupsRefetch]);

  useEffect(() => {
    getResidentGroups();
    getHouseDivisionsByType();
  }, []);

  const allResidentsGroup = useMemo<ResidentGroupPropType>(() => {
    return {
      id: DEFAULT_GROUP_ID,
      name: t('residentGroups_all_residents'),
      residents: [],
      isDefault: true,
    };
  }, [t]);

  useEffect(() => {
    if (residentGroupsError) {
      showApiError(residentGroupsError);
      return;
    }
    if (houseDivisionsError) {
      showApiError(houseDivisionsError);
      return;
    }
    if (residentGroupsData && houseDivisionsData) {
      allResidentsGroup.residents = [...residentGroupsData.getResidents];

      const houseDivisions: HouseDivision[] = houseDivisionsData.houseDivisions;
      const houseDivisionById: Record<string, HouseDivision> = {};
      for (const houseDivision of houseDivisions) {
        houseDivisionById[houseDivision.id] = houseDivision;
      }

      const residents: ResidentPropType[] = residentGroupsData.getResidents;
      const houseDivisionGroups: Record<string, ResidentGroupPropType> = {};
      for (const resident of residents) {
        if (!resident.bed?.id) {
          continue;
        }

        let houseDivision = houseDivisionById[resident.bed.id];
        while (houseDivision?.parentId) {
          houseDivision = houseDivisionById[houseDivision.parentId];
        }

        if (houseDivisionGroups[houseDivision.id]) {
          houseDivisionGroups[houseDivision.id].residents.push(resident);
        } else {
          houseDivisionGroups[houseDivision.id] = {
            id: `${HOUSE_DIVISION_GROUP_ID}${houseDivision.id}`,
            name: houseDivision.name,
            residents: [resident],
            hideEdit: true,
            isDefault: false,
          };
        }
      }

      setResidentGroups([
        allResidentsGroup,
        ...Object.values(houseDivisionGroups),
        ...residentGroupsData.residentGroups,
      ]);
    }
  }, [
    residentGroupsData,
    residentGroupsError,
    houseDivisionsData,
    houseDivisionsError,
    setResidentGroups,
  ]);

  const onAddNewGroup = () => {
    setGroupToEdit(undefined);
    setShowGroupModal(true);
  };

  const onEditGroup = (id: string) => {
    const group = residentGroups.find((g) => g.id === id);
    setGroupToEdit(group);
    setShowGroupModal(true);
  };

  const onCloseGroupModal = () => {
    setShowGroupModal(false);
    setGroupToEdit(undefined);
  };

  const onSaveNewGroup = async (values: ResidentGroupValues) => {
    try {
      await createResidentGroup({ variables: { createResidentGroupInput: values } });
    } catch (error) {
      showApiError(error);
    } finally {
      onCloseGroupModal();
      residentGroupsRefetch && residentGroupsRefetch();
      houseDivisionsRefetch && houseDivisionsRefetch();
    }
  };

  const onSaveEditedGroup = async (values: ResidentGroupUpdateValues) => {
    try {
      await updateResidentGroup({ variables: { data: values } });
    } catch (error) {
      showApiError(error);
    } finally {
      onCloseGroupModal();
      residentGroupsRefetch && residentGroupsRefetch();
      houseDivisionsRefetch && houseDivisionsRefetch();
    }
  };

  const onDeleteGroup = () => {
    setConfirmationText(t('residentGroup_delete_confirmation'));
    setShowDeleteConfirmation(true);
  };

  const onDeleteGroupConfirmed = async (res: boolean) => {
    setConfirmationText('');
    setShowDeleteConfirmation(false);
    if (!res) return;
    if (groupToEdit) {
      await removeResidentGroup({ variables: { id: groupToEdit.id } });
    }
    setShowGroupModal(false);
    setGroupToEdit(undefined);
    residentGroupsRefetch && residentGroupsRefetch();
    houseDivisionsRefetch && houseDivisionsRefetch();
  };

  return (
    <>
      <div className="aside-secondary d-flex flex-row-fluid" style={{ height: '100vh' }}>
        <div className="aside-workspace scroll scroll-push my-2">
          <div className="tab-content d-flex flex-column h-100">
            <AsideMenu onEditGroup={onEditGroup} />
            <Footer className="px-lg-6 p-3">
              <Button
                $lowerCase={true}
                className="w-100"
                onClick={onAddNewGroup}
                disabled={!hasPermission(Rights.CARE.WRITE)}
              >
                {t('residentGroups_createNewGroup')}
              </Button>
            </Footer>
          </div>
        </div>
      </div>
      {showGroupModal && (
        <ResidentGroupModal
          show={showGroupModal}
          isNew={!groupToEdit}
          group={groupToEdit}
          onAddNew={onSaveNewGroup}
          onUpdate={onSaveEditedGroup}
          onDelete={onDeleteGroup}
          onClose={onCloseGroupModal}
        />
      )}
      {showDeleteConfirmation && (
        <ConfirmationModal
          show={showDeleteConfirmation}
          text={confirmationText}
          onResponse={onDeleteGroupConfirmed}
        />
      )}
    </>
  );
};

export default DashboardAside;
